import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { Loader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function ProgramBuilder() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["program"], queryFn: async () => (await api.get("/program")).data });
  const [edits, setEdits] = useState({});
  const [progName, setProgName] = useState("");

  if (isLoading || !data) return <Loader />;

  const saveWeek = async (w) => {
    const e = edits[w.number] || {};
    const payload = { number: w.number, title: e.title ?? w.title, emoji: e.emoji ?? w.emoji, description: e.description ?? w.description };
    try { await api.put(`/program/weeks/${w.number}`, payload); toast.success(`Week ${w.number} saved!`); qc.invalidateQueries({ queryKey: ["program"] }); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const saveProg = async () => {
    await api.put("/program", { name: progName || data.program.name, season: data.program.season, active: true });
    toast.success("Program name updated!"); qc.invalidateQueries({ queryKey: ["program"] });
  };
  const set = (num, field, val) => setEdits((p) => ({ ...p, [num]: { ...p[num], [field]: val } }));

  return (
    <div className="space-y-5" data-testid="program-builder">
      <h2 className="font-head text-3xl text-csh-blue">Program Builder 🗺️</h2>
      <div className="rounded-3xl bg-white p-5 shadow-sm border border-black/5 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-bold uppercase text-muted-foreground">Program Name</label>
          <Input defaultValue={data.program?.name} onChange={(e) => setProgName(e.target.value)} className="rounded-xl border-2 py-6" data-testid="program-name" />
        </div>
        <Button onClick={saveProg} className="rounded-full gradient-orange text-white font-bold mt-5"><Icons.Save size={16} /> Save</Button>
      </div>

      <h3 className="font-head text-xl text-csh-blue">Weeks (edit names & milestones)</h3>
      <div className="space-y-2">
        {data.weeks.map((w) => (
          <div key={w.number} className="flex flex-wrap items-center gap-3 rounded-2xl bg-white p-4 shadow-sm border border-black/5" data-testid={`week-edit-${w.number}`}>
            <span className="flex h-10 w-10 items-center justify-center rounded-xl gradient-purple text-white font-head">{w.number}</span>
            <Input defaultValue={w.emoji} onChange={(e) => set(w.number, "emoji", e.target.value)} className="w-16 rounded-xl border-2 text-center text-xl" />
            <Input defaultValue={w.title} onChange={(e) => set(w.number, "title", e.target.value)} className="flex-1 min-w-[160px] rounded-xl border-2 font-bold" data-testid={`week-title-${w.number}`} />
            <Input defaultValue={w.description} onChange={(e) => set(w.number, "description", e.target.value)} className="flex-1 min-w-[160px] rounded-xl border-2 text-sm" />
            <Button onClick={() => saveWeek(w)} size="sm" className="rounded-full gradient-blue text-white font-bold" data-testid={`save-week-${w.number}`}><Icons.Check size={16} /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
